import { SerializedConstructor } from '@langchain/core/load/serializable';
import { TemplateFormat } from '@langchain/core/prompts';

import { produce } from 'immer';
import { useContext } from 'react';
import { create } from 'zustand';

import {
  defaultEvaluatorOutputSchema,
  emptyStructuredPromptManifest,
} from '@/Pages/HubPlayground/HubPlayground.utils';
import { defaultChatModelManifest } from '@/Pages/Playground/utils/Playground.utils';
import { validateBackfillDate } from '@/Pages/Settings/utils/helpers';
import {
  convertSerializedMessagesToMessageTuple,
  parseOwnerRepoCommit,
} from '@/components/Rules/RulesEvaluatorPane.utils';
import {
  MessageTuple,
  StructuredEvaluatorSchema,
  StructuredEvaluatorSchemaHub,
  StructuredEvaluatorSchemaPrompt,
} from '@/types/schema';
import { toggleTemplateFormat } from '@/utils/template';

import { PROMPT_EXCLUDED_VARIABLES } from '../constants';
import {
  addOrRemoveFewShotFromInputVariables,
  hasFewShotVariable,
  insertFewShotExamples,
  insertFewShotVariable,
  processFewShotExamples,
  removeFewShotVariable,
} from '../utils/fewShotExamplesUtils';
import { getUpdatedMappingFromManifest } from '../utils/getUpdatedMappingFromManifest';
import { schemaHasOneFeedbackProperty } from '../utils/schemaHasOneFeedbackProperty';
import { SchemaDefinition } from '../utils/serializeFeedbackUIToLC';
import {
  formatValidationErrorMessage,
  validateSchemaPropertyKeys,
} from '../utils/validateOutputSchemaKeys';
import { EvaluatorStoreContext } from './EvaluatorStoreProvider';

export type EvaluatorType = 'hub' | 'prompt';
export type FeedbackEditorType = 'pretty' | 'schema' | 'json';
export type EvaluatorStoreType = 'crud' | 'detail' | 'alignPlayground';

// Type guard to determine which type of evaluator we're dealing with
export const isHubEvaluator = (
  evaluator: StructuredEvaluatorSchema
): evaluator is StructuredEvaluatorSchemaHub => {
  return 'hub_ref' in evaluator && evaluator.hub_ref != null;
};

// State interface for the evaluator store
interface EvaluatorState {
  // Common discriminator
  evaluatorType: EvaluatorType;
  evaluatorVersion: number;

  // Hub evaluator fields
  hubRef: string;
  hubRefManifest: any;

  // Prompt evaluator fields
  promptMessages: MessageTuple[] | null;
  feedbackSchema: SchemaDefinition | null;
  promptTemplateFormat: TemplateFormat;

  // common
  variableMapping: Record<string, string> | null;
  model: SerializedConstructor | null;

  // Backfill info
  backfillFrom: string | null;

  // Sampling rate
  samplingRate: number;

  // Few-shot configuration
  fewShotEnabled: boolean;
  fewShotContent: string;
  fewShotExamplesMap: Record<string, any>;
  numFewShotExamples: number;

  // Thread evaluator
  groupBy?: 'thread_id';

  // State management
  isValid: boolean;
  errors: Record<string, string>;
  edited: boolean;
  feedbackEditorType: FeedbackEditorType;
  setFeedbackEditorType: (type: FeedbackEditorType) => void;
  // Hub evaluator actions
  setHubRef: (ref: string) => void;
  setHubRefManifest: (manifest: any) => void;

  // Prompt evaluator actions
  setPromptMessages: (messages: MessageTuple[]) => void;

  setFeedbackSchema: (schema: SchemaDefinition) => void;
  setPromptTemplateFormat: (format: TemplateFormat | undefined) => void;

  setBackfillFrom: (backfillFrom: string | null) => void;

  setSamplingRate: (samplingRate: number) => void;

  // Few-shot configuration actions
  setFewShotEnabled: (enabled: boolean) => void;
  setFewShotContent: (content: string) => void;
  setFewShotExamplesMap: (map: Record<string, any>) => void;
  setNumFewShotExamples: (num: number) => void;

  // common actions
  setVariableMapping: (mapping: Record<string, string>) => void;
  updateMappingWithPromptManifest: (manifest: SerializedConstructor) => void;
  setModel: (model: SerializedConstructor) => void;

  // Import/export methods
  importEvaluator: (
    evaluator?: StructuredEvaluatorSchema,
    ruleSource?: 'session' | 'dataset' | 'thread',
    backfill_from?: string | null,
    evaluator_version?: number | null
  ) => void;
  exportEvaluator: () => StructuredEvaluatorSchema | null;

  // Validation
  validateEvaluator: () => boolean;

  // Reset method
  reset: () => void;

  // Name
  name: string;
  setName: (name: string, isReset?: boolean) => void;

  // Active variable
  activeVariable: string | null;
  setActiveVariable: (variable: string) => void;
  availablePaths: string[] | null;
  setAvailablePaths: (paths: string[]) => void;

  // Evaluator type
  setEvaluatorType: (type: EvaluatorType) => void;
}

// Default state for the store
const defaultState = {
  evaluatorType: 'prompt' as EvaluatorType,
  evaluatorVersion: 2,

  // Hub evaluator defaults
  hubRef: '',
  hubRefManifest: {},

  // Prompt evaluator defaults
  promptMessages: null,
  feedbackSchema: null,
  promptTemplateFormat: 'mustache' as TemplateFormat,

  backfillFrom: null,

  samplingRate: 1,

  // common
  variableMapping: null,
  model: null,

  // Few-shot configuration defaults
  fewShotEnabled: false,
  fewShotContent: '',
  fewShotExamplesMap: {},
  numFewShotExamples: 5,

  // State management defaults
  isValid: false,
  errors: {},
  feedbackEditorType: 'pretty' as FeedbackEditorType,
  edited: false,
  activeVariable: null,
  availablePaths: null,
  // Name
  name: '',
};

const updateFewShotExamplesMap = (
  set: (state: Partial<EvaluatorState>) => void,
  get: () => EvaluatorState,
  newMapping: Record<string, string>,
  originalMapping: Record<string, string>
) => {
  const { fewShotEnabled, fewShotExamplesMap } = get();

  if (fewShotEnabled && Object.keys(fewShotExamplesMap).length > 0) {
    const updatedFewShotExamplesMap = { ...fewShotExamplesMap };

    Object.entries(newMapping).forEach(([key, value]) => {
      // Only update if both the key exists AND the original values match
      // This preserves manually changed few-shot mappings
      if (
        key in updatedFewShotExamplesMap &&
        originalMapping[key] === fewShotExamplesMap[key]
      ) {
        updatedFewShotExamplesMap[key] = value;
      }
    });

    set({ fewShotExamplesMap: updatedFewShotExamplesMap });
  }
};

// Factory function to create evaluator store
const createEvaluatorStore = () =>
  create<EvaluatorState>()((set, get) => ({
    ...defaultState,

    // Hub evaluator actions
    setHubRef: (ref) => {
      set({ hubRef: ref, edited: true });
      get().validateEvaluator();
    },
    setHubRefManifest: (manifest) => {
      if ((get().evaluatorVersion ?? 0) >= 3) {
        set({
          promptMessages: convertSerializedMessagesToMessageTuple(
            manifest.kwargs.first.kwargs.messages
          ),
        });
      }
      set({ hubRefManifest: manifest, edited: true });
      get().validateEvaluator();
    },
    setVariableMapping: (mapping) => {
      // Filter out excluded variables from the mapping
      const filteredMapping = Object.entries(mapping).reduce(
        (acc, [key, value]) => {
          if (!PROMPT_EXCLUDED_VARIABLES.includes(key)) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, string>
      );
      const originalMapping = get().variableMapping ?? {};
      set({ variableMapping: filteredMapping, edited: true });
      updateFewShotExamplesMap(set, get, filteredMapping, originalMapping);
      get().validateEvaluator();
    },
    updateMappingWithPromptManifest: (manifest) => {
      const currentVariableMapping = get().variableMapping ?? {};
      const newMapping = getUpdatedMappingFromManifest(
        manifest,
        currentVariableMapping
      );
      if (newMapping !== currentVariableMapping) {
        set({ variableMapping: newMapping });
        updateFewShotExamplesMap(set, get, newMapping, currentVariableMapping);
        get().validateEvaluator();
      }
    },

    // Common model action
    setModel: (model) => {
      const currentHubRefManifest = get().hubRefManifest;
      const evaluatorVersion = get().evaluatorVersion;
      if (
        Object.keys(currentHubRefManifest).length > 0 &&
        evaluatorVersion >= 3
      ) {
        set({
          hubRefManifest: {
            kwargs: { first: currentHubRefManifest.kwargs.first, last: model },
          },
          model,
          edited: true,
        });
      } else {
        set({ model, edited: true });
      }
      get().validateEvaluator();
    },

    setBackfillFrom: (backfillFrom) => {
      set({ backfillFrom, edited: true });
    },

    setSamplingRate: (samplingRate) => {
      set({ samplingRate, edited: true });
    },

    setPromptMessages: (messages) => {
      set({ promptMessages: messages, edited: true });
      get().validateEvaluator();
    },
    setFeedbackSchema: (schema) => {
      const state = get();

      // Update feedbackSchema for prompt evaluators
      set({
        feedbackSchema: schema,
        edited: true,
      });

      // For hub evaluators, also update the schema in hubRefManifest
      if (state.evaluatorType === 'hub' && state.hubRefManifest) {
        const updatedManifest = produce(state.hubRefManifest, (draft) => {
          const kwargs = draft.kwargs?.first?.kwargs;
          if (kwargs) {
            if (kwargs.schema !== undefined) kwargs.schema = schema;
            if (kwargs.schema_ !== undefined) kwargs.schema_ = schema;
          }
        });

        set({ hubRefManifest: updatedManifest });
      }

      get().validateEvaluator();
    },
    setPromptTemplateFormat: (format) => {
      // If switching from f-string to mustache, update prompt variables
      if (format !== get().promptTemplateFormat) {
        const messages = get().promptMessages?.map((message) => {
          if (typeof message[1] === 'string') {
            // toggle between mustache and f-string formats
            return [
              message[0],
              toggleTemplateFormat(message[1], get().promptTemplateFormat),
            ] as MessageTuple;
          }
          return message;
        });
        set({ promptMessages: messages });
        set({ promptTemplateFormat: format, edited: true });
        get().validateEvaluator();
      }
    },

    // Import method
    importEvaluator: (
      evaluator,
      ruleSource: 'session' | 'dataset' | 'thread' = 'session',
      backfill_from?: string | null,
      evaluator_version?: number | null
    ) => {
      // Reset state first
      get().reset();

      if (evaluator && isHubEvaluator(evaluator)) {
        const hasNotOneFeedbackProperty = !schemaHasOneFeedbackProperty(
          evaluator?.schema
        );
        // It's a hub evaluator
        set({
          evaluatorType: 'hub',
          model:
            (evaluator_version ?? 0) < 3
              ? evaluator.model ?? defaultChatModelManifest
              : null,
          hubRef: evaluator.hub_ref,
          variableMapping: evaluator.variable_mapping || {},
          backfillFrom: backfill_from ?? null,
          edited: false,
          feedbackEditorType: hasNotOneFeedbackProperty ? 'schema' : 'pretty',
          evaluatorVersion: evaluator_version ?? undefined,
        });
      } else {
        const manifestType =
          ruleSource === 'dataset'
            ? 'dataset-eval'
            : ruleSource === 'thread'
            ? 'thread-eval'
            : 'session-eval';

        // Process prompt messages to extract few-shot content if present
        let promptMessages =
          evaluator?.prompt ??
          convertSerializedMessagesToMessageTuple(
            emptyStructuredPromptManifest(
              manifestType,
              null,
              evaluator?.template_format ?? 'mustache'
            ).kwargs.messages
          );

        let fewShotContent = '';
        let fewShotEnabled = false;

        // Check if messages contain {{few_shot_examples}} placeholder
        const hasFewShotPlaceholder = hasFewShotVariable(promptMessages);

        // Extract few-shot content from prompt messages if present
        if (promptMessages) {
          const { processedMessages, fewShotContent: extractedContent } =
            processFewShotExamples(promptMessages);
          promptMessages = processedMessages;
          if (extractedContent) {
            fewShotContent = extractedContent;
            fewShotEnabled = true;
          } else if (hasFewShotPlaceholder) {
            // If there's a placeholder but no content, still enable few-shot
            fewShotEnabled = true;
          }
        }

        // Determine variable mapping
        const variableMapping = evaluator?.variable_mapping || {
          input: 'input',
          output: 'output',
          ...(ruleSource === 'dataset' && { reference: 'referenceOutput' }),
        };

        // Create few-shot examples map based on variable mapping
        const fewShotExamplesMap = fewShotContent ? variableMapping : {};

        const hasNotOneFeedbackProperty =
          evaluator?.schema?.properties &&
          !schemaHasOneFeedbackProperty(evaluator.schema);
        // It's a prompt evaluator
        set({
          evaluatorType: 'prompt',
          evaluatorVersion: evaluator_version ?? undefined,
          model: evaluator?.model ?? defaultChatModelManifest,
          promptMessages,
          feedbackSchema:
            evaluator?.schema ?? defaultEvaluatorOutputSchema(manifestType),
          promptTemplateFormat: evaluator?.template_format ?? 'mustache',
          variableMapping,
          backfillFrom: backfill_from ?? null,
          edited: false,
          feedbackEditorType: hasNotOneFeedbackProperty ? 'schema' : 'pretty',
          // Set few-shot state based on extracted content
          fewShotEnabled,
          fewShotContent,
          fewShotExamplesMap,
          groupBy: ruleSource === 'thread' ? 'thread_id' : undefined,
        });
      }

      get().validateEvaluator();
    },

    // Export method
    exportEvaluator: () => {
      const state = get();

      if (!state.isValid) {
        throw new Error(Object.values(state.errors)[0]);
      }

      if (state.evaluatorType === 'hub') {
        if (!state.model) return null;

        const hubEvaluator: StructuredEvaluatorSchemaHub = {
          hub_ref: state.hubRef,
          model: state.model,
          schema: state.feedbackSchema ?? undefined,
        };

        if (Object.keys(state?.variableMapping ?? {}).length > 0) {
          hubEvaluator.variable_mapping = state.variableMapping ?? {};
        }

        return hubEvaluator;
      } else {
        if (!state.model || !state.promptMessages || !state.feedbackSchema)
          return null;

        // Process messages to insert few-shot examples if enabled
        let processedPromptMessages = [...state.promptMessages];

        if (state.fewShotEnabled && state.fewShotContent) {
          // Insert few-shot examples into the messages
          processedPromptMessages = insertFewShotExamples(
            processedPromptMessages,
            state.fewShotContent
          );
        } else if (!state.fewShotEnabled) {
          // If few-shot is disabled, make sure to remove any {{few_shot_examples}} variables
          processedPromptMessages = removeFewShotVariable(
            processedPromptMessages
          );
        }

        const promptEvaluator: StructuredEvaluatorSchemaPrompt = {
          model: state.model,
          prompt: processedPromptMessages,
          schema: state.feedbackSchema,
        };

        if (state.promptTemplateFormat) {
          promptEvaluator.template_format = state.promptTemplateFormat;
        }

        if (Object.keys(state.variableMapping ?? {}).length > 0) {
          promptEvaluator.variable_mapping = state.variableMapping ?? {};
        }

        return promptEvaluator;
      }
    },

    // Validation method
    validateEvaluator: () => {
      const state = get();
      const errors: Record<string, string> = {};

      if (state.evaluatorType === 'hub') {
        // Validate hub evaluator
        if (!state.hubRef) {
          errors.hubRef = 'Hub reference is required';
        }

        if (!state.model) {
          errors.model = 'Model is required';
        }

        // Validate output schema property keys for hub evaluators
        if (state.feedbackSchema) {
          const { isValid, invalidKeys } = validateSchemaPropertyKeys(
            state.feedbackSchema
          );
          if (!isValid) {
            errors.outputSchemaKeys = formatValidationErrorMessage(invalidKeys);
          }
        }
      } else {
        // Validate prompt evaluator
        if (!state.model) {
          errors.model = 'Model is required';
        }

        if (
          (state.promptMessages ?? []).length === 0 &&
          (state.evaluatorVersion ?? 0) < 3
        ) {
          errors.promptMessages = 'At least one message is required';
        }

        if (
          !state.feedbackSchema ||
          Object.keys(state.feedbackSchema).length === 0
        ) {
          errors.promptSchema = 'Schema is required';
        } else {
          // Validate output schema property keys
          const { isValid, invalidKeys } = validateSchemaPropertyKeys(
            state.feedbackSchema
          );
          if (!isValid) {
            errors.outputSchemaKeys = formatValidationErrorMessage(invalidKeys);
          }
        }
      }

      if (Object.keys(state.variableMapping ?? {}).length === 0) {
        errors.variableMapping = 'At least one variable mapping is required';
      } else {
        // Check for undefined, null or empty string mapping values
        const unmappedVariables = Object.entries(state.variableMapping ?? {})
          .filter(
            ([key, value]) => !value && !PROMPT_EXCLUDED_VARIABLES.includes(key)
          )
          .map(([key]) => key);

        if (unmappedVariables.length > 0) {
          errors.variableMapping = `Some template variables are not mapped: ${unmappedVariables.join(
            ', '
          )}`;
        }
      }

      // Validate backfill date if present
      if (state.backfillFrom) {
        const validationResult = validateBackfillDate(state.backfillFrom);

        if (validationResult !== true) {
          errors.backfillFrom = validationResult as string;
        }
      }

      const isValid = Object.keys(errors).length === 0;

      set({ isValid, errors });

      return isValid;
    },

    // Reset method
    reset: () => {
      set(defaultState);
    },

    // Name
    setName: (name, isReset = false) => set({ name, edited: !isReset }),

    // Active variable
    setActiveVariable: (variable) => set({ activeVariable: variable }),

    // Available paths
    setAvailablePaths: (paths) => set({ availablePaths: paths }),

    // Feedback editor type
    setFeedbackEditorType: (type) => {
      const schema = get().feedbackSchema;
      const hasNotOneFeedbackProperty =
        !schema?.properties ||
        Object.keys(schema.properties).filter((key) => key !== 'comment')
          .length !== 1;

      if (hasNotOneFeedbackProperty && type === 'pretty') {
        set({ feedbackEditorType: 'schema' });
      } else {
        set({ feedbackEditorType: type });
      }
    },

    // Evaluator type
    setEvaluatorType: (evaluatorType) => set({ evaluatorType }),

    // Few-shot configuration actions
    setFewShotEnabled: (enabled) => {
      const state = get();

      // Handle prompt evaluators
      if (state.evaluatorType === 'prompt' && state.promptMessages) {
        const updatedMessages = enabled
          ? insertFewShotVariable(state.promptMessages)
          : removeFewShotVariable(state.promptMessages);

        if (enabled && !state.fewShotContent) {
          // When enabling few-shot without content, initialize the examples map
          // based on variable mapping
          const variableMapping = state.variableMapping || {};
          const fewShotExamplesMap = {
            ...Object.entries(variableMapping).reduce((acc, [key, value]) => {
              acc[key] = value;
              return acc;
            }, {} as Record<string, string>),
          };

          set({
            fewShotEnabled: enabled,
            fewShotExamplesMap,
            promptMessages: updatedMessages,
            edited: true,
          });
        } else {
          set({
            fewShotEnabled: enabled,
            promptMessages: updatedMessages,
            edited: true,
          });
        }
      }
      // Handle hub evaluators
      else if (state.evaluatorType === 'hub' && state.hubRefManifest) {
        try {
          const updatedManifest = produce(state.hubRefManifest, (draft) => {
            const messages = draft.kwargs?.first?.kwargs?.messages;
            if (!messages) return;

            // Convert manifest messages to message tuples
            const promptMessages =
              convertSerializedMessagesToMessageTuple(messages);

            // Apply few-shot variable insertion/removal
            const updatedPromptMessages = enabled
              ? insertFewShotVariable(promptMessages)
              : removeFewShotVariable(promptMessages);

            const inputVariables = draft.kwargs.first.kwargs.input_variables;
            const updatedInputVariables = addOrRemoveFewShotFromInputVariables(
              inputVariables,
              enabled
            );
            draft.kwargs.first.kwargs.input_variables = updatedInputVariables;

            // Update messages in-place inside the draft
            updatedPromptMessages.forEach(([_, messageContent], index) => {
              const message = messages[index];
              if (message?.kwargs?.prompt) {
                message.kwargs.prompt.kwargs.template = messageContent;
                const messageInputVariables =
                  message.kwargs.prompt.kwargs.input_variables;
                const updatedMessageInputVariables =
                  addOrRemoveFewShotFromInputVariables(
                    messageInputVariables,
                    enabled
                  );
                message.kwargs.prompt.kwargs.input_variables =
                  updatedMessageInputVariables;
              }
            });
          });

          if (enabled && !state.fewShotContent) {
            // When enabling few-shot without content, initialize the examples map
            const variableMapping = state.variableMapping || {};
            const fewShotExamplesMap = {
              ...Object.entries(variableMapping).reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
              }, {} as Record<string, string>),
            };

            set({
              fewShotEnabled: enabled,
              fewShotExamplesMap,
              hubRefManifest: updatedManifest,
              edited: true,
            });
          } else {
            set({
              fewShotEnabled: enabled,
              hubRefManifest: updatedManifest,
              edited: true,
            });
          }
        } catch (error) {
          // If updating the manifest fails, just update the enabled state
          set({ fewShotEnabled: enabled, edited: true });
        }
      }
      // Fallback for other cases
      else {
        set({ fewShotEnabled: enabled, edited: true });
      }

      get().validateEvaluator();
    },
    setFewShotContent: (content) => {
      set({ fewShotContent: content, edited: true });
      get().validateEvaluator();
    },
    setFewShotExamplesMap: (map) => {
      set({ fewShotExamplesMap: map, edited: true });
      get().validateEvaluator();
    },
    setNumFewShotExamples: (num) => {
      set({ numFewShotExamples: num, edited: true });
      get().validateEvaluator();
    },
  }));

export const useEvaluatorCrudStore = createEvaluatorStore();
export const useEvaluatorDetailStore = createEvaluatorStore();
export const useEvaluatorPlaygroundStore = createEvaluatorStore();

// Determine which store to use based on context
export const useEvaluatorStore = <T,>(
  selector: (state: EvaluatorState) => T
) => {
  const storeType = useContext(EvaluatorStoreContext);
  const crudResult = useEvaluatorCrudStore(selector);
  const detailResult = useEvaluatorDetailStore(selector);
  const playgroundResult = useEvaluatorPlaygroundStore(selector);

  // 'crud' is the default - we use this in a bunch of places, but the evaluator detail page is only in a single place.
  // To be safe, we'll just use the EvaluatorStoreProvider to wrap the detail page and then use the crud store everywhere
  // this context is not set.
  switch (storeType) {
    case 'detail':
      return detailResult;
    case 'alignPlayground':
      return playgroundResult;
    default:
      return crudResult;
  }
};

export const usePromptName = () => {
  const hubRef = useEvaluatorStore((state) => state.hubRef);
  const [, promptName, commitHash] = hubRef ? parseOwnerRepoCommit(hubRef) : [];

  return { promptName, commitHash };
};
